// ImportedItemsPanel.jsx — read-only list of the items written by Inventory Import (inventory_items).
// W-28 (2026-10-06): import saved rows that no web screen read. This lists GET /inventory/items as the
// server returns it: every field in the response is shown, none are added or renamed in meaning.
// The server returns the newest 1000 rows (ORDER BY id DESC LIMIT 1000) and can filter by category.
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card, Stack, Chip, Typography, Alert, Button, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, TablePagination,
} from "@mui/material";
import ApiCall from "../../utils/apicall";
import Scrollbar from "../scrollbar";

const SERVER_LIMIT = 1000;

// Known fields first (in the order the import writes them); any other field the server returns follows.
const KNOWN_ORDER = [
  "id", "name", "category", "description", "sku", "quantity", "unit", "unit_cost", "vendor", "po_number",
  "received_date", "location", "source", "source_doc_id", "ai_confidence", "notes",
  "created_by_name", "created_by_user_id", "tenant_id", "createdAt", "updatedAt", "deletedAt",
];

const columnsOf = (rows) => {
  const seen = new Set();
  rows.forEach((r) => Object.keys(r || {}).forEach((k) => seen.add(k)));
  const known = KNOWN_ORDER.filter((k) => seen.has(k));
  const rest = [...seen].filter((k) => !KNOWN_ORDER.includes(k)).sort();
  return [...known, ...rest];
};

const show = (v) => {
  if (v === null || v === undefined || v === "") return "-";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

export default function ImportedItemsPanel({ refreshKey = 0 }) {
  const [category, setCategory] = useState("all");
  const [categories, setCategories] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const reqSeq = useRef(0); // only the latest request may update the list

  const load = useCallback(async () => {
    const seq = ++reqSeq.current;
    setLoading(true);
    setError(null);
    try {
      const r = await ApiCall.get("/inventory/items", {
        params: category === "all" ? undefined : { category },
      });
      if (seq !== reqSeq.current) return;
      const data = r?.data?.data || {};
      setRows(Array.isArray(data.items) ? data.items : []);
      if (Array.isArray(data.categories)) setCategories(data.categories);
      setPage(0);
    } catch (e) {
      if (seq !== reqSeq.current) return;
      setRows([]);
      setError(e?.response?.data?.message || e?.message || "Could not load imported items.");
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, [category]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const cols = columnsOf(rows);

  return (
    <Card sx={{ p: 2 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        Items saved by <strong>Import Inventory</strong>. This list is read-only and shows the newest{" "}
        {SERVER_LIMIT} imported items at most.
      </Alert>

      {categories.length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {["all", ...categories].map((c) => (
            <Chip key={c} label={c === "all" ? "All" : c}
              color={category === c ? "primary" : "default"}
              variant={category === c ? "filled" : "outlined"}
              onClick={() => setCategory(c)} />
          ))}
        </Stack>
      )}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Loading imported items...</Typography>
        </Stack>
      ) : error ? (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
          Imported items could not be loaded: {error}
        </Alert>
      ) : rows.length === 0 ? (
        <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
          <Typography variant="h6">No imported items</Typography>
          <Typography variant="body2" color="text.secondary">
            {category === "all"
              ? "Nothing has been imported yet. Use Import Inventory to add items from a file or a receiving document."
              : `No imported items in the category "${category}".`}
          </Typography>
        </Stack>
      ) : (
        <>
          {rows.length >= SERVER_LIMIT && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Showing the newest {SERVER_LIMIT} items only; older imported items are not listed here.
            </Alert>
          )}
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {cols.map((c) => <TableCell key={c}><b>{c}</b></TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((r, i) => (
                    <TableRow hover key={r.id ?? `${page}-${i}`}>
                      {cols.map((c) => <TableCell key={c}>{show(r[c])}</TableCell>)}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setPage(0); setRowsPerPage(parseInt(e.target.value, 10)); }}
          />
        </>
      )}
    </Card>
  );
}
